import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listNotifications, listAnnouncements, markNotificationRead, markAllNotificationsRead, markAnnouncementRead } from '../api';
import { Notification, Announcement } from '../types';
import { useNotifications } from '../contexts/NotificationContext';
import { NOTIFICATION_TYPE_COLORS, ANNOUNCEMENT_TYPE_COLORS, getNotificationTypeLabel, getAnnouncementTypeLabel } from '../constants/notificationLabels';
import { formatTime } from '../utils/formatTime';
import Breadcrumb from '../components/Breadcrumb';

type TabType = 'notifications' | 'announcements';

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation('notifications');
  const { refreshUnread, unread, readSyncVersion } = useNotifications();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabType>('notifications');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nTotal, setNTotal] = useState(0);
  const [aTotal, setATotal] = useState(0);
  const [nPage, setNPage] = useState(1);
  const [aPage, setAPage] = useState(1);
  const pageSize = 20;

  // 请求序号：列表数据有两个写入方（用户翻页/切 tab 的加载 + 跨 tab 已读同步的静默重拉），
  // 只允许最后发起的请求落地，防止慢响应覆盖新数据
  const fetchSeqRef = useRef(0);

  const loadNotifications = useCallback(() => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError('');
    listNotifications(nPage, pageSize)
      .then(res => {
        if (seq !== fetchSeqRef.current) return;
        setNotifications(res.items || []);
        setNTotal(res.total);
      })
      .catch(() => { if (seq === fetchSeqRef.current) setError(t('page.loadNotificationsFailed')); })
      .finally(() => { if (seq === fetchSeqRef.current) setLoading(false); });
  }, [nPage, t]);

  const loadAnnouncements = useCallback(() => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError('');
    listAnnouncements(aPage, pageSize)
      .then(res => {
        if (seq !== fetchSeqRef.current) return;
        setAnnouncements(res.items || []);
        setATotal(res.total);
      })
      .catch(() => { if (seq === fetchSeqRef.current) setError(t('page.loadAnnouncementsFailed')); })
      .finally(() => { if (seq === fetchSeqRef.current) setLoading(false); });
  }, [aPage, t]);

  useEffect(() => {
    if (tab === 'notifications') loadNotifications();
    else loadAnnouncements();
  }, [tab, loadNotifications, loadAnnouncements]);

  // 其他 tab/设备标记已读 → 静默重拉当前列表（不置 loading，避免界面闪烁）
  const isInitialSync = useRef(true);
  useEffect(() => {
    if (isInitialSync.current) {
      isInitialSync.current = false;
      return;
    }
    const seq = ++fetchSeqRef.current;
    if (tab === 'notifications') {
      listNotifications(nPage, pageSize)
        .then(res => {
          if (seq !== fetchSeqRef.current) return;
          setNotifications(res.items || []);
          setNTotal(res.total);
        })
        .catch(() => console.warn('[notifications] read-sync refresh failed'))
        .finally(() => { if (seq === fetchSeqRef.current) setLoading(false); });
    } else {
      listAnnouncements(aPage, pageSize)
        .then(res => {
          if (seq !== fetchSeqRef.current) return;
          setAnnouncements(res.items || []);
          setATotal(res.total);
        })
        .catch(() => console.warn('[notifications] read-sync refresh failed'))
        .finally(() => { if (seq === fetchSeqRef.current) setLoading(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readSyncVersion]);

  const handleTabChange = (newTab: TabType) => {
    if (newTab === tab) return;
    setTab(newTab);
    if (newTab === 'notifications') setNPage(1);
    else setAPage(1);
  };

  const handleReadNotification = async (id: string) => {
    await markNotificationRead(id).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, readAt: new Date().toISOString() } : n));
    refreshUnread();
  };

  const handleReadAnnouncement = async (id: string) => {
    await markAnnouncementRead(id).catch(() => {});
    setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, isRead: true } : a));
    refreshUnread();
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead().catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
    refreshUnread();
  };

  const handleKeyDown = (e: React.KeyboardEvent, onClick: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const nTotalPages = Math.ceil(nTotal / pageSize);
  const aTotalPages = Math.ceil(aTotal / pageSize);

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.5rem 1.25rem',
    fontSize: '0.88rem',
    fontWeight: active ? 600 : 400,
    color: active ? 'var(--primary)' : 'var(--text-3)',
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  });

  const badgeStyle: React.CSSProperties = {
    fontSize: '0.7rem',
    padding: '0.05rem 0.4rem',
    borderRadius: '8px',
    fontWeight: 600,
    color: '#fff',
    background: 'var(--danger)',
    marginLeft: '0.3rem',
  };

  return (
    <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('page.breadcrumbHome'), to: '/projects' }, { label: t('page.breadcrumbTitle') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('page.pageTitle')}</h1>
        {tab === 'notifications' && unread.notifications > 0 && (
          <button className="btn btn-ghost" onClick={handleMarkAllRead} style={{ fontSize: '0.82rem' }}>
            {t('page.markAllRead')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '1rem' }} role="tablist">
        <button role="tab" aria-selected={tab === 'notifications'} style={tabStyle(tab === 'notifications')} onClick={() => handleTabChange('notifications')}>
          {t('page.tabNotifications')}
          {unread.notifications > 0 && <span style={badgeStyle}>{unread.notifications}</span>}
        </button>
        <button role="tab" aria-selected={tab === 'announcements'} style={tabStyle(tab === 'announcements')} onClick={() => handleTabChange('announcements')}>
          {t('page.tabAnnouncements')}
          {unread.announcements > 0 && <span style={badgeStyle}>{unread.announcements}</span>}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('page.loading')}</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--danger)' }}>{error}</div>
      ) : tab === 'notifications' ? (
        <>
          {notifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('page.emptyNotifications')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }} role="tabpanel">
              {notifications.map(n => {
                const isUnread = !n.readAt;
                const isTicketLink = n.relatedType === 'ticket' && !!n.relatedId;
                const isClickable = isUnread || isTicketLink;
                const typeColor = NOTIFICATION_TYPE_COLORS[n.type] || 'var(--text-3)';
                const typeLabel = getNotificationTypeLabel(n.type);
                const clickHandler = () => {
                  if (isUnread) handleReadNotification(n.id);
                  if (n.relatedType === 'ticket' && n.relatedId) {
                    navigate(`/tickets/${n.relatedId}`);
                  }
                };
                return (
                  <div
                    key={n.id}
                    className="card"
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onClick={clickHandler}
                    onKeyDown={isClickable ? e => handleKeyDown(e, clickHandler) : undefined}
                    style={{
                      padding: '0.85rem 1.25rem',
                      cursor: isClickable ? 'pointer' : 'default',
                      background: isUnread ? 'var(--primary-light)' : undefined,
                      transition: 'background var(--transition-fast)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                      {isUnread && (
                        <span style={{
                          width: '7px', height: '7px', borderRadius: '50%',
                          background: 'var(--primary)', flexShrink: 0,
                        }} />
                      )}
                      <span style={{
                        fontSize: '0.72rem', padding: '0.1rem 0.45rem',
                        borderRadius: '4px', fontWeight: 600,
                        color: typeColor,
                        background: `color-mix(in srgb, ${typeColor} 10%, transparent)`,
                      }}>
                        {typeLabel}
                      </span>
                      {n.priority === 'high' && (
                        <span style={{
                          fontSize: '0.68rem', padding: '0.05rem 0.35rem',
                          borderRadius: '3px', fontWeight: 600,
                          color: 'var(--danger)', border: '1px solid var(--danger)',
                        }}>
                          {t('page.important')}
                        </span>
                      )}
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                        {formatTime(n.createdAt)}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.95rem', fontWeight: isUnread ? 600 : 400 }}>{n.title}</div>
                    {n.content && (
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-3)', marginTop: '0.2rem' }}>
                        {n.content}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {nTotalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button className="btn btn-ghost" disabled={nPage <= 1} onClick={() => setNPage(p => p - 1)} style={{ fontSize: '0.8rem' }}>{t('page.prevPage')}</button>
              <span style={{ lineHeight: '2rem', fontSize: '0.8rem', color: 'var(--text-3)' }}>{nPage} / {nTotalPages}</span>
              <button className="btn btn-ghost" disabled={nPage >= nTotalPages} onClick={() => setNPage(p => p + 1)} style={{ fontSize: '0.8rem' }}>{t('page.nextPage')}</button>
            </div>
          )}
        </>
      ) : (
        <>
          {announcements.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('page.emptyAnnouncements')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }} role="tabpanel">
              {announcements.map(a => {
                const aTypeColor = ANNOUNCEMENT_TYPE_COLORS[a.type] || ANNOUNCEMENT_TYPE_COLORS.update;
                const aTypeLabel = getAnnouncementTypeLabel(a.type);
                const clickHandler = () => !a.isRead && handleReadAnnouncement(a.id);
                return (
                  <div
                    key={a.id}
                    className="card"
                    role={a.isRead ? undefined : 'button'}
                    tabIndex={a.isRead ? undefined : 0}
                    onClick={clickHandler}
                    onKeyDown={a.isRead ? undefined : e => handleKeyDown(e, clickHandler)}
                    style={{
                      padding: '0.85rem 1.25rem',
                      cursor: a.isRead ? 'default' : 'pointer',
                      background: a.isRead ? undefined : 'var(--primary-light)',
                      transition: 'background var(--transition-fast)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                      {!a.isRead && (
                        <span style={{
                          width: '7px', height: '7px', borderRadius: '50%',
                          background: 'var(--primary)', flexShrink: 0,
                        }} />
                      )}
                      <span style={{
                        fontSize: '0.72rem', padding: '0.1rem 0.45rem',
                        borderRadius: '4px', fontWeight: 600,
                        color: aTypeColor,
                        background: `color-mix(in srgb, ${aTypeColor} 10%, transparent)`,
                      }}>
                        {aTypeLabel}
                      </span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                        {formatTime(a.publishedAt)}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.95rem', fontWeight: a.isRead ? 400 : 600 }}>{a.title}</div>
                    {a.content && (
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-3)', marginTop: '0.2rem' }}>
                        {a.content}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {aTotalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button className="btn btn-ghost" disabled={aPage <= 1} onClick={() => setAPage(p => p - 1)} style={{ fontSize: '0.8rem' }}>{t('page.prevPage')}</button>
              <span style={{ lineHeight: '2rem', fontSize: '0.8rem', color: 'var(--text-3)' }}>{aPage} / {aTotalPages}</span>
              <button className="btn btn-ghost" disabled={aPage >= aTotalPages} onClick={() => setAPage(p => p + 1)} style={{ fontSize: '0.8rem' }}>{t('page.nextPage')}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationsPage;
